import { useQuery } from '@apollo/client'
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  GET_MARKET_AUDIENCES,
  MARKET_AUDIENCES_CONTEXT,
  MarketAudiencesData
} from '../../data/gql/questionnaire/queries/marketAudiences'
import { LoggerErrorType } from '../../data/logger'
import { useSurveyId } from '../../hooks/useSurveyId'
import { captureApolloError } from '../../utils/HelperFunctions'
import { useProjectType } from '../Project/Project.hooks'
import { useProjectMarkets } from './MarketVisibility/useProjectMarkets'
import { toMarketAudience } from './audienceDisplayLogic'
import {
  MarketAudience,
  MarketAudienceContext,
  emptyMarketAudience
} from './marketAudienceContext'

const pendingMarketAudience: MarketAudience = {
  ...emptyMarketAudience,
  status: 'loading'
}

const failedMarketAudience: MarketAudience = {
  ...emptyMarketAudience,
  status: 'failed'
}

interface LoaderProps {
  onLoad: (marketAudience: MarketAudience) => void
}

const MarketAudienceQuery = memo(function MarketAudienceQuery({
  onLoad
}: LoaderProps) {
  const surveyId = useSurveyId()
  const markets = useProjectMarkets()
  const { data, loading, error } = useQuery<MarketAudiencesData>(
    GET_MARKET_AUDIENCES,
    {
      context: MARKET_AUDIENCES_CONTEXT,
      variables: { surveyId },
      fetchPolicy: 'no-cache',
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloQuery,
          'marketAudiences',
          error
        )
      }
    }
  )

  const marketAudience = useMemo(() => {
    if (error) {
      return failedMarketAudience
    }
    return loading || markets.length === 0
      ? pendingMarketAudience
      : toMarketAudience(data, surveyId, markets)
  }, [data, loading, error, surveyId, markets])

  useEffect(() => {
    onLoad(marketAudience)
  }, [marketAudience, onLoad])

  return null
})

const MarketAudienceLoader = memo(function MarketAudienceLoader({
  onLoad
}: LoaderProps) {
  const { hasMarkets } = useProjectType()

  useEffect(() => {
    if (!hasMarkets) {
      onLoad(emptyMarketAudience)
    }
  }, [hasMarkets, onLoad])

  return hasMarkets ? <MarketAudienceQuery onLoad={onLoad} /> : null
})

/** Serves every market's audience to the page from one query. */
export const MarketAudienceProvider = ({
  children = null
}: {
  children?: ReactNode
}) => {
  const [isRequested, setIsRequested] = useState(false)
  const [marketAudience, setMarketAudience] = useState(pendingMarketAudience)
  const request = useCallback(() => setIsRequested(true), [])
  const value = useMemo(
    () => ({ marketAudience, request }),
    [marketAudience, request]
  )

  return (
    <MarketAudienceContext.Provider value={value}>
      {isRequested && <MarketAudienceLoader onLoad={setMarketAudience} />}
      {children}
    </MarketAudienceContext.Provider>
  )
}
